import React from 'react';
import {Metric} from 'web-vitals';
import {createRoot} from 'react-dom/client';
import {VerdocsEndpoint} from '@verdocs/js-sdk';
import {datadogRum} from '@datadog/browser-rum';
import {datadogLogs} from '@datadog/browser-logs';
import {onLCP, onINP, onCLS, onFCP, onTTFB} from 'web-vitals';
import {AnalyticsManager} from './Managers/AnalyticsManager';
import pkg from '../package.json';
import {CONFIG} from './config';
import App from './App';

import './index.css';
import 'react-tooltip/dist/react-tooltip.css';

AnalyticsManager.init();

if (window.location.hostname !== 'localhost') {
  datadogLogs.init({
    clientToken: 'pub1bcbc40fde481ca335be5fdb272ecc35',
    site: 'datadoghq.com',
    service: 'web-app',
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    env: CONFIG.IS_BETA ? 'stage' : 'production',
    forwardConsoleLogs: 'all',
    // Per https://docs.datadoghq.com/logs/log_collection/javascript/#discard-specific-logs this is the
    // correct usage, but the method signature expects a boolean to always be returned.
    // @ts-ignore
    beforeSend: (event, _context) => {
      // Filter out some nuisance logs
      if (event.message?.includes('LaunchDarkly') || event.message?.includes('linkedin.com')) {
        return false;
      }

      // We recently began noticing "sseError" messages in our logs with references to Binance.
      // This was alarming at first because it was suspicious but we determined that Datadog was
      // picking up errors from browser extensions and reporting them to us. This is apparently
      // (https://github.com/DataDog/browser-sdk/issues/2534) a known issue without a solution,
      // but this workaround is OK for now.
      /**
       * func sseError not found
       * callback id: 1772027416018 not found
       * _setResponse id 1772027416018 result undefined
       * [initMessageListener] --- receive contentscript message --- { "type": "functionCallResult", "target": "inPage", "messageId": "5vkcB0kv-sI4n_-ZYq4O3", "__wallet_sent_from_content_script_msg__": true }
       * [FunctionCallError] sseError [ { "logs": [], "error": { "code": 0, "message": "[object Event]", "url": "https://wallet.binance.com/tonbridge/bridge/events?client_id=8....&user_info=" } } ] func sseError not found
       * [initMessageListener] --- receive contentscript message --- { "type": "functionCallResult", "error": "func sseError not found", "target": "inPage", "messageId": "FjlwPV9GrMpnqSzveNBzy", "__wallet_sent_from_content_script_msg__": true }
       * [postMessageToContentScript] { "type": "contentscriptFunctionCall", "moduleName": "providerAdapter", "funcName": "reportError", "args": [ { "code": 0, "message": "Failed to connect TON bridge, please try it later or contact us for further support" } ], "callFrom": "inPage", "providerCallContext": { "networkType": "TON" }, "messageId": "5vkcB0kv-sI4n_-ZYq4O3" }
       * [postMessageToContentScript] { "type": "contentscriptFunctionCall", "moduleName": "providerAdapter", "funcName": "sseError", "args": [ { "logs": [], "error": { "code": 0, "message": "[object Event]", "url": "https://wallet.binance.com/tonbridge/bridge/events?client_id=8....." } } ], "callFrom": "inPage", "providerCallContext": { "networkType": "TON" }, "messageId": "FjlwPV9GrMpnqSzveNBzy" }
       * setting id 1772027416127
       * _request called sseError { "logs": [], "error": { "code": 0, "message": "[object Event]", "url": "https://wallet.binance.com/tonbridge/bridge/events?client_id=86312f9b8f836004dc9841d8a1e3093338ac7ff2014f4e8efe4ac9ef695ef16b&user_info=" } }
       * An error occurred: { "type": "error", "isTrusted": true, "currentTarget": "[EventSource]", "target": "[EventSource]" }
       * error { "type": "error", "isTrusted": true, "currentTarget": "[EventSource]", "target": "[EventSource]" }
       * listen https://wallet.binance.com/tonbridge/bridge/events?client_id=86312f9b8f836004dc9841d8a1e3093338ac7ff2014f4e8efe4ac9ef695ef16b&user_info=
       * restart sse, about to listen
       */
      // if (event.type === 'error' && context.error) {
      //   const errorMessage = context.error.message;
      //   const errorStack = context.error.stack;
      //
      //   // Example 1: Filter by specific error messages (use RegExp for exact match)
      //   const ignoredErrors = ['chrome-extension://', 'Extension error:', 'Specific unwanted message'];
      //
      //   for (const ignoredMsg of ignoredErrors) {
      //     if (errorMessage.includes(ignoredMsg) || (errorStack && errorStack.includes(ignoredMsg))) {
      //       console.log('Datadog RUM: Ignoring browser extension error:', errorMessage);
      //       return null; // Return null to drop the error event
      //     }
      //   }
      //
      //   // Example 2: Filter errors from specific domains/source files (if available in the stack trace)
      //   if (errorStack && errorStack.includes('moz-extension://')) {
      //     console.log('Datadog RUM: Ignoring Firefox extension error');
      //     return null;
      //   }
      // }

      return event; // Return the event object to send it to Datadog
    },
  });

  datadogRum.init({
    applicationId: '8332cf3b-8e61-4513-9837-153a8e114adc',
    clientToken: 'pub130dd06de7e2cc49470f434e171f121f',
    site: 'datadoghq.com',
    service: 'web-app',
    env: CONFIG.IS_BETA ? 'stage' : 'production',
    version: pkg.version,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
  });
}

const endpoint = VerdocsEndpoint.getDefault() as VerdocsEndpoint;
endpoint //
  .setTimeout(30000)
  .setBaseURL(CONFIG.API_URL)
  .setClientID('verdocs-web')
  .logRequests(true);

const container = document.getElementById('root') || document.createElement('div');
const root = createRoot(container);
root.render(<App />);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
const metricDetails = {
  // See https://web.dev/fcp/. Has any portion of the page rendered?
  FCP: {name: 'First Contentful Paint', good: 1800, warning: 3000},

  // See https://web.dev/lcp/. Has the largest chunk of content rendered?
  LCP: {name: 'Largest Contentful Paint', good: 2500, warning: 4000},

  // See https://web.dev/cls/. Is the page moving around on the user?
  CLS: {name: 'Cumulative Layout Shift', good: 0.1, warning: 0.25},

  // See https://web.dev/inp/. Is the user trying to click on things whose event handlers are not yet "ready"?
  INP: {name: 'Interaction to Next Paint', good: 200, warning: 500},

  // See https://web.dev/fid/. Is the user trying to click on things whose event handlers are not yet "ready"?
  FID: {name: 'First Input Delay', good: 100, warning: 300},

  // See https://web.dev/time-to-first-byte/. Is the server slow?
  TTFB: {name: 'Time to First Byte', good: 500, warning: 1000},
};

const reportWebVitals = (metric: Metric) => {
  const details = metricDetails[metric.name];
  console.log(`${details.name}: ${Math.floor(metric.value)}`);
};

onCLS(reportWebVitals);
onINP(reportWebVitals);
onLCP(reportWebVitals);
onFCP(reportWebVitals);
onTTFB(reportWebVitals);
